import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { TicketAirType } from "@afidium/ts-facade";
import { Yii } from "libraries/interfaces/Yii";
import { INotification, NotificationType } from "libraries/interfaces/INotification";
import { ILoader } from "libraries/component/loader/ILoader";
import { wenji } from "libraries/interfaces/IWenji";
import { IconDefinition } from "@fortawesome/free-regular-svg-icons";
import { faAngleDown, faAngleUp } from "@fortawesome/free-solid-svg-icons";
import { TicketingTableHelperService } from "./ticketing-table-helper.service";
import { MatTable } from '@angular/material/table';
import { TicketingTableDomainService } from "./ticketing-table-domain.service";
import {
    IConfigurableTableColumnHmpr,
    IConfigurableTableDefaultSort,
    IConfigurableTableHmpr,
} from "libraries/interfaces/i-configurable-table";
import { EConfigurableTableFields } from "libraries/pipes/configurable-table/compute-value.pipe";
import { IPageChange, IPaginationRecordset } from "libraries/component/pagination/IPagination";
import { IMenuItem, IPopupMenuConfig } from "libraries/component/popup-menu/popup-menu.component";
import { ISortOutput, SortDirection } from "libraries/interfaces/ISort";
import { ISorterWording } from "libraries/component/sorter/sorter.component";
import { BtnStyleTypes } from "libraries/ui_button/btn.component";
import { IApiSorting, SortKey } from "../../ticketing-report-domain.service";
import _cloneDeep from "lodash-es/cloneDeep";
import { LabelService } from "libraries/service/label/label.service";

export enum EFixedColumn {
    eventDate = "eventDate",
    ticketNumber = "ticketNumber",
    system = "system",
    type = "type",
    purpose = "purpose",
    foldAction = "foldAction"
}

export const MIN_HMPR_TOTAL: number = 100000;

class Wording {
    public readonly title: string = Yii.t('wui-sasha-js', "Journal d'émissions - Résultats");
    public readonly noResult: string = Yii.t('wui-sasha-js', "Aucun résultat pour cette recherche");
    public readonly [EFixedColumn.eventDate]: string = Yii.t("wui-sasha-js", "Date");
    public readonly [EFixedColumn.ticketNumber]: string = Yii.t("wui-sasha-js", "Billet");
    public readonly [EFixedColumn.system]: string = Yii.t("wui-sasha-js", "Système");
    public readonly [EFixedColumn.type]: string = Yii.t("wui-sasha-js", "Type");
    public readonly [EFixedColumn.purpose]: string = Yii.t("wui-sasha-js", "Action");
    public readonly author: string = Yii.t("wui-sasha-js", "Auteur");
    public readonly price: string = Yii.t("wui-sasha-js", "Prix");
    public readonly traveller: string = Yii.t("wui-sasha-js", "Voyageur");
    public readonly beginDate: string = Yii.t("wui-sasha-js", "Départ");
    public readonly UP: string = Yii.t("wui-sasha-js", "UP");
    public readonly gdsReference: string = Yii.t("wui-sasha-js", "Dossier GDS");
    public readonly bookingReference: string = Yii.t("wui-sasha-js", "Dossier");
    public readonly export: string = Yii.t('wui-sasha-js', 'Exporter');
    public readonly resultsLabel: string = Yii.t('wui-sasha-js', 'résultats de %first% à %last% / %total%');
    public readonly resultsLabelOnePage: string = Yii.t('wui-sasha-js', 'résultats de %first% à %last%');
    public readonly resultsLabelInfo: string = Yii.t('wui-sasha-js', '(maximum atteint)');
}

const CONFIG_SORT_ITEM: { [key: string]: IMenuItem } = {
    [EConfigurableTableFields.UP]: {
        label: 'UP',
        actionName: EConfigurableTableFields.UP
    },
    [EConfigurableTableFields.AUTHOR]: {
        label: Yii.t('wui-sasha-js', 'Auteur'),
        actionName: EConfigurableTableFields.AUTHOR
    },
    [EConfigurableTableFields.BEGIN]: {
        label: Yii.t('wui-sasha-js', 'Départ'),
        actionName: EConfigurableTableFields.BEGIN
    },
    [EConfigurableTableFields.TRAVELLER]: {
        label: Yii.t('wui-sasha-js', 'Voyageur'),
        actionName: EConfigurableTableFields.TRAVELLER
    },
    [EConfigurableTableFields.BOOKING]: {
        label: Yii.t('wui-sasha-js', 'Dossier'),
        actionName: EConfigurableTableFields.BOOKING
    },
    [EConfigurableTableFields.GDS_BOOKING]: {
        label: Yii.t('wui-sasha-js', 'Dossier GDS'),
        actionName: EConfigurableTableFields.GDS_BOOKING
    },
    [EConfigurableTableFields.COST_TOTAL]: {
        label: Yii.t('wui-sasha-js', 'Prix'),
        actionName: EConfigurableTableFields.COST_TOTAL
    },

};

@Component({
    selector: 'ticketing-table',
    template: `
        <card>
            <div class="card-header-content">
                <div class="header-left-part">
                    <h1>{{wording.title }}</h1>
                    <div *ngIf="ticketArrayTable?.length">{{displayedResultLabel}}</div>
                </div>
                <div class="ticket-search-form-btn-container">
                    <sorter *ngIf="sorterMenuConfig?.items?.length"
                            [menuConfig]="sorterMenuConfig"
                            [disabled]="!ticketArray?.length"
                            [selectedActionName]="apiSorting.fieldKey"
                            [sortDirection]="apiSorting.order"
                            (onSort)="sort($event)"></sorter>

                    <ng-container *permissionIf="exportPermission">
                        <btn [disableIf]="!ticketArrayTable?.length"
                             [value]="wording.export"
                             [type]="exportBtnType"
                             (onClick)="exportSearchResult()"></btn>
                    </ng-container>

                </div>
            </div>
            <div class="card-body-content" *ngIf="notification?.active">
                <notification [type]="notification.type">{{notification.message}}</notification>
            </div>
            <div class="card-body-content" *ngIf="loader?.active">
                <loader [message]="loader.message"></loader>
            </div>
            <div class="card-body-content" *ngIf="ticketArray && !ticketArray.length && !loader?.active">
                <notification [type]="notificationType.Info">{{wording.noResult}}</notification>
            </div>
            <div class="card-body-content" *ngIf="ticketArrayTable?.length && tableConfig">

                <notification [type]="exportNotification.type" *ngIf="exportNotification?.active">
                    <span [innerHTML]="exportNotification.message"></span>
                </notification>
                <loader [message]="exportLoader.message" *ngIf="exportLoader.active"></loader>

                <table mat-table [dataSource]="ticketArrayTable" #myTable>
                    <!-- FIXED COLS -->
                    <ng-container [matColumnDef]="fixedColumnEnum.eventDate">
                        <th-text *matHeaderCellDef [isLeft]="true">{{wording.eventDate}}</th-text>
                        <ng-container *matCellDef="let element; let rowIndex = index; let odd = odd">
                            <td-text *ngIf="!odd" [isOdd]="rowIndex % 4 === 0" [isLightColor]="true">
                                {{(element.actionArray && element.actionArray[0].when.date) | date: shortDateFormat}}
                            </td-text>
                            <td-text *ngIf="odd && toggledRows[rowIndex-1]"
                                     [colspan]="displayedColumns.length+1"
                                     [isWhite]="true">
                                <ticketing-detail [ticket]="ticketArrayTable[rowIndex-1]"></ticketing-detail>
                            </td-text>
                        </ng-container>
                    </ng-container>
                    <ng-container [matColumnDef]="fixedColumnEnum.ticketNumber">
                        <th-text *matHeaderCellDef [isLeft]="true">{{wording.ticketNumber}}</th-text>
                        <ng-container *matCellDef="let element; let rowIndex = index; let odd = odd">
                            <td-text *ngIf="!odd" [isOdd]="rowIndex % 4 === 0" [isLightColor]="true">
                                <ng-container *ngIf="element | computeBookingLink ; else noBookingLink">
                                    <a [routerLink]="element | computeBookingLink" fragment="travellers"
                                       class="booking-link">{{element.number}}</a>
                                </ng-container>
                                <ng-template #noBookingLink>
                                    {{element.number}}
                                </ng-template>
                            </td-text>
                        </ng-container>

                    </ng-container>
                    <ng-container [matColumnDef]="fixedColumnEnum.system">
                        <th-text *matHeaderCellDef [isLeft]="true">{{wording.system}}</th-text>
                        <ng-container *matCellDef="let element; let rowIndex = index; let odd = odd">
                            <td-text *ngIf="!odd" [isOdd]="rowIndex % 4 === 0" [isLightColor]="true">
                                {{element.host?.code | hostAlias | async}}
                            </td-text>
                        </ng-container>

                    </ng-container>
                    <ng-container [matColumnDef]="fixedColumnEnum.type">
                        <th-text *matHeaderCellDef [isLeft]="true">{{wording.type}}</th-text>
                        <ng-container *matCellDef="let element; let rowIndex = index; let odd = odd">
                            <td-text *ngIf="!odd" [isOdd]="rowIndex % 4 === 0" [isLightColor]="true">
                                {{element.xIssue}}
                            </td-text>
                        </ng-container>
                    </ng-container>
                    <ng-container [matColumnDef]="fixedColumnEnum.purpose">
                        <th-text *matHeaderCellDef [isLeft]="true">{{wording.purpose}}</th-text>
                        <ng-container *matCellDef="let element; let rowIndex = index; let odd = odd">
                            <td-text *ngIf="!odd" [isOdd]="rowIndex % 4 === 0" [isLightColor]="true">
                                <ng-container *ngIf="element.actionArray?.length">
                                    {{element.actionArray[0].xPurpose | transcodeActionType}}
                                </ng-container>
                            </td-text>
                        </ng-container>
                    </ng-container>

                    <!-- ADDITIONAL COLS -->
                    <ng-container *ngFor="let colKey of additionalDisplayedColumns">
                        <ng-container [matColumnDef]="colKey">
                            <th-text [isLeft]="true"
                                     *matHeaderCellDef>{{(tableConfig | findColInConfig:colKey).defaultLabel }}</th-text>
                            <ng-container *matCellDef="let element; let rowIndex = index; let odd = odd">
                                <td-text *ngIf="!odd" [isOdd]="rowIndex % 4 === 0" [isLightColor]="true">
                                    <div *ngFor="let fieldKey of (tableConfig | findColInConfig:colKey).fieldKeyArray">
                                        <ng-container *ngIf="(element | computeValue:fieldKey)">

                                            <ng-container
                                                    *ngIf="(element | computeValue:fieldKey).type === 'string'">
                                                <ng-container *ngIf="(element | computeValue:fieldKey).link">
                                                    <a [routerLink]="(element | computeValue:fieldKey).link"
                                                       class="booking-link">{{(element | computeValue:fieldKey).value}}</a>
                                                </ng-container>
                                                <ng-container *ngIf="!(element | computeValue:fieldKey).link">
                                                    {{(element | computeValue:fieldKey).value}}
                                                </ng-container>

                                            </ng-container>
                                            <ng-container
                                                    *ngIf="(element | computeValue:fieldKey).type === 'number'">
                                                {{(element | computeValue:fieldKey).value}}
                                            </ng-container>
                                            <ng-container
                                                    *ngIf="(element | computeValue:fieldKey).type === 'price'">
                                                <price-real-amount
                                                        *ngIf="(element | computeValue:fieldKey) && (element | computeValue:fieldKey).price"
                                                        [showZero]="true"
                                                        [value]="(element | computeValue:fieldKey).price.amount"
                                                        [currency]="(element | computeValue:fieldKey).price.currency"></price-real-amount>
                                            </ng-container>
                                        </ng-container>

                                    </div>
                                </td-text>
                            </ng-container>
                        </ng-container>
                    </ng-container>

                    <!-- FIXED COL -->
                    <ng-container [matColumnDef]="fixedColumnEnum.foldAction">
                        <th-text *matHeaderCellDef [isLeft]="true"></th-text>
                        <ng-container *matCellDef="let element; let rowIndex = index; let odd = odd">
                            <td-text *ngIf="!odd"
                                     [isOdd]="rowIndex % 4 === 0"
                                     [alignCenter]="true"
                                     [isLightColor]="true"
                                     [hasClickAction]="true"
                                     (onClick)="toggle(rowIndex)">
                                <fa-icon [icon]="iconDown" *ngIf="!toggledRows[rowIndex]"></fa-icon>
                                <fa-icon [icon]="iconUp" *ngIf="toggledRows[rowIndex]"></fa-icon>
                            </td-text>
                        </ng-container>
                    </ng-container>

                    <ng-container *ngIf="tableConfig.isVisibleHeader">
                        <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
                    </ng-container>
                    <ng-container *matRowDef="let row; columns: displayedColumns;let odd=odd; let rowIndex=index">
                        <tr mat-row [hidden]="odd && !toggledRows[rowIndex-1]"></tr>
                    </ng-container>

                </table>
                <div class="pagination-container">
                    <pagination *ngIf="ticketArrayTable?.length"
                                [listLength]="limitedRecordset?.nbrecord"
                                [recordset]="limitedRecordset"
                                (onPageChange)="onPageChange.emit($event)"></pagination>
                </div>
            </div>
        </card>

    `,
    styles: [`
        .collapser {
            text-align: center;
            cursor: pointer;
        }

        table {
            margin-top: 0;
        }

        .ticket-search-form-btn-container {
            display: flex;
            align-items: center;
        }

        .pagination-container {
            margin: 0 auto;
        }

        .header-left-part {
            display: flex;
            align-items: baseline;
        }

        .header-left-part div {
            font-size: 1rem;
            margin-left: .75rem;
            color: #414141;
        }

        a.booking-link {
            color: #337ab7
        }
    `]
})
export class TicketingTableComponent implements OnChanges {
    @Input()
    public ticketArray: TicketAirType[];

    @Input()
    public notification: INotification;

    @Input()
    public loader: ILoader;

    @Input()
    public listLength: number;

    @Input()
    public recordset: IPaginationRecordset;

    @Input()
    public exportNotification: INotification;

    @Input()
    public exportLoader: ILoader;

    @Input()
    public apiSorting: IApiSorting = {
        fieldKey: SortKey.eventDate,
        order: SortDirection.descending,
    };

    @Output()
    public readonly onPageChange: EventEmitter<IPageChange> = new EventEmitter();

    @Output()
    public readonly onSort: EventEmitter<ISortOutput> = new EventEmitter<ISortOutput>();

    @Output()
    public readonly onExport: EventEmitter<void> = new EventEmitter<void>();

    @ViewChild(MatTable, {static: true})
    public myTable: MatTable<any>;

    public readonly shortDateFormat: string = JSON.parse(wenji.ui.dateFormat).short;
    public readonly timeFormat: string = JSON.parse(wenji.ui.dateFormat).time;
    public readonly iconUp: IconDefinition = faAngleUp;
    public readonly iconDown: IconDefinition = faAngleDown;
    public readonly fixedColumnEnum: typeof EFixedColumn = EFixedColumn;
    public readonly displayedColumnsStart: string[] = [
        EFixedColumn.eventDate,
        EFixedColumn.ticketNumber,
        EFixedColumn.system,
        EFixedColumn.type,
        EFixedColumn.purpose,
        EFixedColumn.foldAction,
    ];
    public readonly notificationType: typeof NotificationType = NotificationType;
    public readonly wording: Wording = new Wording();
    public readonly sorterWording: ISorterWording = {
        [SortDirection.descending]: "Z - A",
        [SortDirection.ascending]: "A - Z",
    };

    public tableConfig: IConfigurableTableHmpr;
    public displayedColumns: string[] = [];
    public additionalDisplayedColumns: string[] = [];

    public ticketArrayTable: TicketAirType[];

    public toggledRows: boolean[] = [];

    public sorterMenuConfig: IPopupMenuConfig;

    public readonly exportBtnType: BtnStyleTypes = BtnStyleTypes.actionDo;

    public readonly exportPermission: string = "sasha.ticketingReport.search.export.use";

    public canEmitSort: boolean = false;

    public limitedRecordset: IPaginationRecordset;

    public displayedResultLabel: string;

    constructor(
        private readonly ticketingTableHelperService: TicketingTableHelperService,
        private readonly ticketingTableDomainService: TicketingTableDomainService,
        private readonly labelService: LabelService,
    ) {
    }


    public ngOnChanges(changes: SimpleChanges): void {
        if (changes?.ticketArray?.currentValue) {
            this.toggledRows = [];
            if (!this.tableConfig) {
                this.computeCols();
            }
            this.ticketArrayTable = this.ticketingTableHelperService.divideLineByAction(this.ticketArray);
        }

        if (changes.recordset?.currentValue) {
            this.limitedRecordset = _cloneDeep(this.recordset);
            this.limitedRecordset.total = Math.min(MIN_HMPR_TOTAL, this.recordset.total);

            const last: number = this.recordset.first + this.recordset.nbrecord;
            const total: number = this.recordset.total;
            const first: number = this.recordset.first + 1;

            this.displayedResultLabel = this.labelService.completeLabel(
                this.wording.resultsLabel,
                {
                    first,
                    last: Math.min(last, total),
                    total
                }
            );

            if (this.limitedRecordset.total === last) {
                this.displayedResultLabel += ` ${this.wording.resultsLabelInfo}`;
            }
        }
    }

    public computeCols(): void {
        this.ticketingTableDomainService.getTableConfiguration()
            .subscribe({
                next: (configuration: IConfigurableTableHmpr): void => {
                    const hardItems: IMenuItem[] = [
                        {
                            label: this.wording[EFixedColumn.eventDate],
                            actionName: SortKey.eventDate,
                        },
                        {
                            label: this.wording[EFixedColumn.ticketNumber],
                            actionName: SortKey.ticketNumber,
                        },
                        {
                            label: this.wording[EFixedColumn.system],
                            actionName: SortKey.system,
                        },
                        {
                            label: this.wording[EFixedColumn.type],
                            actionName: SortKey.type,
                        },
                        {
                            label: this.wording[EFixedColumn.purpose],
                            actionName: SortKey.purpose,
                        },
                    ];
                    const configItems: IMenuItem[] = configuration.defaultSortArray ?
                        configuration.defaultSortArray
                            .filter(
                                (sortItem: IConfigurableTableDefaultSort): boolean => Boolean(CONFIG_SORT_ITEM[sortItem.fieldKey])
                            )
                            .map(
                                (sortItem: IConfigurableTableDefaultSort): IMenuItem => CONFIG_SORT_ITEM[sortItem.fieldKey]
                            ) :
                        [];
                    this.sorterMenuConfig = {
                        buttonLabel: Yii.t('wui-sasha-js', 'Trier par'),
                        items: [...hardItems, ...configItems],
                    };
                    this.tableConfig = configuration;
                    this.additionalDisplayedColumns = configuration.columnArray
                        .filter((col: IConfigurableTableColumnHmpr): boolean => col.isDisplayed)
                        .map((col: IConfigurableTableColumnHmpr): string => col.key);
                    this.displayedColumns = [
                        EFixedColumn.eventDate,
                        EFixedColumn.ticketNumber,
                        EFixedColumn.system,
                        EFixedColumn.type,
                        EFixedColumn.purpose,
                        ...this.additionalDisplayedColumns,
                        EFixedColumn.foldAction
                    ];

                }
            });
    }

    public toggle(rowIndex: number): void {
        this.toggledRows[rowIndex] = !this.toggledRows[rowIndex];
    }

    public exportSearchResult(): void {
        this.onExport.emit();
    }

    public sort(sortOutput: ISortOutput): void {
        console.log(this.canEmitSort)
        //avoid emit the first time, to avoid double search on F5
        if (this.canEmitSort) {
            this.onSort.emit(sortOutput);
        }

        this.canEmitSort = true;

    }

}
